/**
 * 웹·데스크탑 공용 파일 저장(§4.3, T-2.02·T-2.04).
 *
 * - 웹: 기존과 같이 Blob + `<a download>`.
 * - 데스크탑: WKWebView/WebView2는 `<a download>`를 조용히 무시하거나 위치를 고를 수 없다. 바이트를 Rust로 보내고,
 *   Rust가 저장 대화상자를 열어 **사용자가 고른 경로에만** 쓴다. 웹뷰에는 fs 권한이 없다.
 */
import { invoke, isDesktop } from "./runtime";

export type SaveOutcome = "saved" | "cancelled";

function webDownload(fileName: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  // 일부 브라우저는 click 직후 revoke하면 받기가 끊긴다. 다음 틱에 푼다.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function toBytes(data: ArrayBuffer | Uint8Array | Blob): Promise<Uint8Array> {
  if (data instanceof Uint8Array) return data;
  if (data instanceof Blob) return new Uint8Array(await data.arrayBuffer());
  return new Uint8Array(data);
}

/** 바이트 저장. 데스크탑에서 사용자가 취소하면 "cancelled". */
export async function saveBytes(fileName: string, data: ArrayBuffer | Uint8Array | Blob, mimeType: string): Promise<SaveOutcome> {
  if (!isDesktop()) {
    webDownload(fileName, data instanceof Blob ? data : new Blob([data as BlobPart], { type: mimeType }));
    return "saved";
  }
  const bytes = await toBytes(data);
  const res = await invoke<{ saved: boolean }>("save_file", bytes, {
    headers: { "x-xplanner-file-name": encodeURIComponent(fileName) },
  });
  return res.saved ? "saved" : "cancelled";
}

export function saveText(fileName: string, text: string, mimeType = "application/json"): Promise<SaveOutcome> {
  return saveBytes(fileName, new TextEncoder().encode(text), mimeType);
}

function dataUrlToBytes(url: string): { bytes: Uint8Array; mime: string } | null {
  const m = /^data:([^;,]*)(;base64)?,(.*)$/s.exec(url);
  if (!m) return null;
  const mime = m[1] || "application/octet-stream";
  const body = m[3] ?? "";
  if (m[2]) {
    const bin = atob(body);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return { bytes, mime };
  }
  return { bytes: new TextEncoder().encode(decodeURIComponent(body)), mime };
}

/**
 * 첨부 다운로드(`<a href download>`)의 데스크탑 대체. data:/blob: 주소를 바이트로 바꿔 저장한다.
 * 웹에서는 false를 돌려 원래 링크 동작을 그대로 둔다.
 */
export async function saveFromUrl(fileName: string, url: string): Promise<SaveOutcome | false> {
  if (!isDesktop()) return false;
  if (url.startsWith("data:")) {
    const parsed = dataUrlToBytes(url);
    if (!parsed) return false;
    return saveBytes(fileName, parsed.bytes, parsed.mime);
  }
  if (url.startsWith("blob:")) {
    const blob = await (await fetch(url)).blob();
    return saveBytes(fileName, blob, blob.type || "application/octet-stream");
  }
  if (/^https?:/i.test(url)) {
    await invoke("open_external_url", { url });
    return "saved";
  }
  return false;
}

/** `<a download>`에 붙이는 클릭 처리. 데스크탑이면 기본 동작을 막고 저장 대화상자로 보낸다. */
export function desktopDownloadClick(fileName: string, url: string) {
  return (e: { preventDefault: () => void }) => {
    if (!isDesktop()) return;
    e.preventDefault();
    void saveFromUrl(fileName, url);
  };
}
